import { ReadyState } from "react-use-websocket";
import { State } from "../stores/ideaNodesStore";

// Constants for WebSocket connection management
export const WS_CONSTANTS = {
  POLL_INTERVAL_MS: 250,
  RECONNECT_WAIT_TIME_MS: 3000,
  MAX_RECONNECT_ATTEMPTS: 4,
  TOTAL_TIMEOUT_MS: 30000,
  STALE_PROMISE_TIMEOUT_MS: 600,
} as const;

// Custom error types for better error handling
export class WebSocketError extends Error {
  constructor(message: string, public readonly code: string) {
    super(message);
    this.name = "WebSocketError";
  }
}

/**
 * Creates a timeout promise that rejects after specified time
 */
export function createTimeout(ms: number, message: string): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new WebSocketError(message, "TIMEOUT"));
    }, ms);
  });
}

/**
 * Polls for WebSocket OPEN state with proper cleanup
 */
export function pollForOpen(
  maxWaitMs: number,
  signal?: AbortSignal,
  getStoreState: () => State
): Promise<boolean> {
  return new Promise<boolean>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new WebSocketError("Operation aborted", "ABORTED"));
      return;
    }

    let timer: NodeJS.Timeout | null = null;
    let timeout: NodeJS.Timeout | null = null;

    const cleanup = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    };

    // Cleanup on abort
    signal?.addEventListener("abort", () => {
      cleanup();
      reject(new WebSocketError("Operation aborted", "ABORTED"));
    });

    timer = setInterval(() => {
      const state = getStoreState();
      if (state.wsReadyState === ReadyState.OPEN) {
        cleanup();
        resolve(true);
      }
    }, WS_CONSTANTS.POLL_INTERVAL_MS);

    // Ensure cleanup after max wait time
    timeout = setTimeout(() => {
      cleanup();
      resolve(false);
    }, maxWaitMs);
  });
}

/**
 * Attempts to establish WebSocket connection with multiple retries
 */
export async function waitForConnectionMultiAttempt(
  maxAttempts: number,
  signal?: AbortSignal,
  getStoreState: () => State,
  setStoreState: (updates: Partial<State>) => void
): Promise<void> {
  const store = getStoreState();

  // Check for existing connection attempt
  if (store.activeConnectionPromise) {
    try {
      await Promise.race([
        store.activeConnectionPromise,
        createTimeout(
          WS_CONSTANTS.STALE_PROMISE_TIMEOUT_MS,
          "Existing connection attempt timed out"
        ),
      ]);
      return;
    } catch (err) {
      // Clear stale promise and continue with new attempt
      setStoreState({ activeConnectionPromise: null });
    }
  }

  const connectionPromise = new Promise<void>(async (resolve, reject) => {
    try {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (signal?.aborted) {
          throw new WebSocketError("Connection attempt aborted", "ABORTED");
        }

        const currentState = getStoreState().wsReadyState;

        // Already open, we're done
        if (currentState === ReadyState.OPEN) {
          return resolve();
        }

        // If connecting, wait to see if it completes
        if (currentState === ReadyState.CONNECTING) {
          console.log("WebSocket is connecting, waiting...");
          const opened = await pollForOpen(
            WS_CONSTANTS.RECONNECT_WAIT_TIME_MS,
            signal,
            getStoreState
          );
          if (opened) {
            return resolve();
          }
          console.log("Connection attempt timed out while CONNECTING");
        }

        // Force reconnection if not open
        const reconnectWs = getStoreState().reconnectWs;
        if (!reconnectWs) {
          throw new WebSocketError(
            "No reconnectWs function available",
            "CONFIGURATION"
          );
        }

        console.log(
          `Attempt ${attempt}/${maxAttempts}: Initiating reconnection...`
        );
        reconnectWs();

        const opened = await pollForOpen(
          WS_CONSTANTS.RECONNECT_WAIT_TIME_MS,
          signal,
          getStoreState
        );
        if (opened) {
          return resolve();
        }

        console.log(`Attempt ${attempt} failed to establish connection`);
      }

      throw new WebSocketError(
        `Failed to connect after ${maxAttempts} attempts`,
        "MAX_ATTEMPTS"
      );
    } catch (err) {
      reject(toError(err));
    }
  });

  // Set up cleanup
  connectionPromise.finally(() => {
    setStoreState({ activeConnectionPromise: null });
  });

  // Store the promise for deduplication
  setStoreState({ activeConnectionPromise: connectionPromise });

  // Race against total timeout
  return Promise.race([
    connectionPromise,
    createTimeout(WS_CONSTANTS.TOTAL_TIMEOUT_MS, "Total connection timeout"),
  ]);
}

/**
 * Utility to ensure we always get a proper Error object
 */
export function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  return new Error(String(err));
}
